// @ts-nocheck
import { useState } from 'react';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import {
  Stack,
  Paper,
  Text,
  Group,
  Loader,
  Center,
  Code,
  Divider,
  SimpleGrid,
  Badge,
  Button,
  Modal,
  TextInput,
  Textarea,
} from '@mantine/core';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { useForm } from '@mantine/form';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { notifications } from '@mantine/notifications';
import { IconShieldCheck, IconEdit, IconTrash } from '@tabler/icons-react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageHeader } from '../../components/PageHeader';
import { StatusBadge } from '../../components/StatusBadge';
import { EmptyState } from '../../components/EmptyState';
import {
  usePolicy,
  useUpdatePolicy,
  useDeletePolicy,
} from '../../hooks/usePolicies';

interface PolicyDetail {
  id: string;
  originalId?: string;
  version?: number;
  isActive?: boolean;
  policyType: string;
  name: string;
  description: string;
  status: string;
  config: Record<string, unknown> | null;
  employeeCount?: number;
  createdAt?: string;
  createdBy?: string;
}

function formatPolicyType(type: string): string {
  return type
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function ConfigSection({ config }: { config: Record<string, unknown> }) {
  const entries = Object.entries(config);

  if (entries.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        No configuration defined.
      </Text>
    );
  }

  // Check if any value is an object (nested config)
  const hasNestedObjects = entries.some(
    ([, v]) => typeof v === 'object' && v !== null && !Array.isArray(v),
  );

  if (hasNestedObjects) {
    return (
      <Stack gap="md">
        {entries.map(([key, value]) => (
          <Paper key={key} p="sm" withBorder>
            <Text size="sm" fw={600} mb="xs" tt="capitalize">
              {key.replace(/_/g, ' ')}
            </Text>
            {typeof value === 'object' && value !== null ? (
              <Code block>{JSON.stringify(value, null, 2)}</Code>
            ) : (
              <Text size="sm">{String(value)}</Text>
            )}
          </Paper>
        ))}
      </Stack>
    );
  }

  return (
    <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
      {entries.map(([key, value]) => (
        <Group key={key} justify="space-between" p="xs">
          <Text size="sm" c="dimmed" tt="capitalize">
            {key.replace(/_/g, ' ')}
          </Text>
          <Text size="sm" fw={500}>
            {typeof value === 'boolean'
              ? value
                ? 'Yes'
                : 'No'
              : String(value ?? '-')}
          </Text>
        </Group>
      ))}
    </SimpleGrid>
  );
}

export function PolicyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: policy, loading: isLoading } = usePolicy(id);

  const updateMutation = useUpdatePolicy();
  const deleteMutation = useDeletePolicy();

  // Edit modal state
  const [editOpen, setEditOpen] = useState(false);

  // Delete modal state
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');

  const editForm = useForm({
    initialValues: {
      name: '',
      description: '',
      config: '',
    },
    validate: {
      name: (v: string) => (v.trim() ? null : 'Name is required'),
      config: (v: string) => {
        if (!v.trim()) return null;
        try {
          JSON.parse(v);
          return null;
        } catch {
          return 'Config must be valid JSON';
        }
      },
    },
  });

  const openEdit = () => {
    if (!policy) return;
    editForm.setValues({
      name: policy.name,
      description: policy.description || '',
      config: policy.config ? JSON.stringify(policy.config, null, 2) : '',
    });
    setEditOpen(true);
  };

  const handleEdit = editForm.onSubmit((values) => {
    if (!id) return;
    const input: Record<string, unknown> = {
      name: values.name.trim(),
    };
    if (values.description.trim()) {
      input.description = values.description.trim();
    } else {
      input.description = '';
    }
    if (values.config.trim()) {
      try {
        input.config = JSON.parse(values.config);
      } catch {
        // validated above
      }
    }

    updateMutation.mutate({ id, input })
      .then(() => {
        notifications.show({
          title: 'Policy Updated',
          message: 'A new version has been created (append-only).',
          color: 'green',
        });
        setEditOpen(false);
      })
      .catch((err: unknown) => {
        notifications.show({
          title: 'Error',
          message: err instanceof Error ? err.message : 'Failed to update policy',
          color: 'red',
        });
      });
  });

  const handleDelete = () => {
    if (!id || !deleteReason.trim()) return;
    deleteMutation.mutate({ id })
      .then(() => {
        notifications.show({
          title: 'Policy Deactivated',
          message: 'Policy soft-deleted (new inactive version created).',
          color: 'green',
        });
        setDeleteOpen(false);
        setDeleteReason('');
        navigate('/admin/workforce/leave/policies');
      })
      .catch((err: unknown) => {
        notifications.show({
          title: 'Error',
          message: err instanceof Error ? err.message : 'Failed to delete policy',
          color: 'red',
        });
      });
  };

  if (isLoading) {
    return (
      <Center py="xl">
        <Loader size="lg" />
      </Center>
    );
  }

  if (!policy) {
    return (
      <Stack gap="md">
        <PageHeader
          title="Policy Not Found"
          breadcrumbs={[
            { label: 'Policies', to: '/admin/workforce/leave/policies' },
            { label: 'Not Found' },
          ]}
        />
        <EmptyState
          icon={<IconShieldCheck size={48} stroke={1.5} color="var(--mantine-color-dimmed)" />}
          title="Policy Not Found"
          message="The requested policy could not be found."
        />
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <PageHeader
        title={policy.name}
        breadcrumbs={[
          { label: 'Policies', to: '/admin/workforce/leave/policies' },
          { label: policy.name },
        ]}
        actions={
          <Group gap="sm">
            {policy.version != null && (
              <Badge variant="light" color="gray" size="lg">
                v{policy.version}
              </Badge>
            )}
            <Button
              icon={<IconEdit size={16} />}
              variant="light"
              onClick={openEdit}
            >
              Edit
            </Button>
            <Button
              icon={<IconTrash size={16} />}
              variant="light"
              color="red"
              onClick={() => setDeleteOpen(true)}
            >
              Deactivate
            </Button>
          </Group>
        }
      />

      <Paper p="lg" withBorder>
        <Text size="lg" fw={600} mb="md">
          Policy Information
        </Text>
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          <Group justify="space-between">
            <Text size="sm" c="dimmed">
              Name
            </Text>
            <Text size="sm" fw={500}>
              {policy.name}
            </Text>
          </Group>
          <Group justify="space-between">
            <Text size="sm" c="dimmed">
              Type
            </Text>
            <Text size="sm" fw={500}>
              {formatPolicyType(policy.policyType)}
            </Text>
          </Group>
          <Group justify="space-between">
            <Text size="sm" c="dimmed">
              Status
            </Text>
            <StatusBadge status={policy.status} />
          </Group>
          {policy.version != null && (
            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                Version
              </Text>
              <Badge variant="light" color="gray">
                v{policy.version}
              </Badge>
            </Group>
          )}
          {policy.employeeCount != null && (
            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                Employees
              </Text>
              <Text size="sm" fw={500}>
                {policy.employeeCount}
              </Text>
            </Group>
          )}
          {policy.createdAt && (
            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                Created
              </Text>
              <Text size="sm" fw={500}>
                {new Date(policy.createdAt).toLocaleDateString()}
              </Text>
            </Group>
          )}
        </SimpleGrid>

        {policy.description && (
          <>
            <Divider my="md" />
            <Text size="sm" fw={600} mb="xs">
              Description
            </Text>
            <Text size="sm" c="dimmed">
              {policy.description}
            </Text>
          </>
        )}
      </Paper>

      <Paper p="lg" withBorder>
        <Text size="lg" fw={600} mb="md">
          Configuration
        </Text>
        {policy.config ? (
          <ConfigSection config={policy.config} />
        ) : (
          <Text size="sm" c="dimmed">
            No configuration data available for this policy.
          </Text>
        )}
      </Paper>

      {/* ── Edit Policy Modal ──────────────────────────────────────────── */}
      <Modal
        opened={editOpen}
        onClose={() => {
          setEditOpen(false);
          editForm.reset();
        }}
        title={`Edit Policy: ${policy.name}`}
        size="lg"
      >
        <form onSubmit={handleEdit}>
          <Stack gap="md">
            {policy.version != null && (
              <Text size="xs" c="dimmed">
                Saving will create a new version (v{policy.version + 1}). The previous
                version is preserved in the append-only audit log.
              </Text>
            )}

            <TextInput
              label="Name"
              placeholder="Policy name"
              required
              {...editForm.getInputProps('name')}
            />

            <Textarea
              label="Description"
              placeholder="Optional description..."
              minRows={2}
              {...editForm.getInputProps('description')}
            />

            <Textarea
              label="Configuration (JSON)"
              placeholder='{"key": "value"}'
              minRows={4}
              autosize
              maxRows={12}
              styles={{ input: { fontFamily: 'monospace' } }}
              {...editForm.getInputProps('config')}
            />

            <Paper p="sm" bg="blue.0" radius="sm">
              <Text size="xs" c="blue.7">
                Append-only: Saving creates a new version. The previous version remains
                immutable in the audit trail.
              </Text>
            </Paper>

            <Group justify="flex-end">
              <Button
                variant="default"
                onClick={() => {
                  setEditOpen(false);
                  editForm.reset();
                }}
              >
                Cancel
              </Button>
              <Button type="submit" loading={updateMutation.isPending}>
                Save New Version
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      {/* ── Delete Confirmation Modal ──────────────────────────────────── */}
      <Modal
        opened={deleteOpen}
        onClose={() => {
          setDeleteOpen(false);
          setDeleteReason('');
        }}
        title="Deactivate Policy"
        size="sm"
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            This will soft-delete{' '}
            <Text span fw={600}>
              {policy.name}
            </Text>{' '}
            by creating a new inactive version (append-only). The original record is preserved
            for audit.
          </Text>
          <Textarea
            label="Reason for deactivation"
            placeholder="Required for audit trail..."
            required
            value={deleteReason}
            onChange={(e) => setDeleteReason(e.currentTarget.value)}
            minRows={2}
          />
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => {
                setDeleteOpen(false);
                setDeleteReason('');
              }}
            >
              Keep Policy
            </Button>
            <Button
              color="red"
              disabled={!deleteReason.trim()}
              loading={deleteMutation.isPending}
              onClick={handleDelete}
            >
              Deactivate Policy
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
